/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useIntl } from 'react-intl-next';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	contentContainerBase: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		textAlign: 'center',
		marginTop: token('space.400'),
		paddingTop: token('space.1000'),
		transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
	},
	contentContainer: {
		opacity: 0,
		transform: 'translateY(40px)',
	},
	contentContainerVisible: {
		opacity: 1,
		transform: 'translateY(0)',
	},
	ctaContainer: {
		display: 'flex',
		gap: token('space.100'),
		justifyContent: 'center',
		flexWrap: 'wrap',
	},
});

export type WelcomeContentProps = {
	isVisible?: boolean;
	onClose: () => void;
	onGetStarted: () => void;
	testId?: string;
	userName?: string;
} & WelcomeContentActionProps;

export type WelcomeContentActionProps =
	| {
			href: string;
			onGetStarted: () => void;
			type: 'link';
	  }
	| {
			onGetStarted: () => void;
			type: 'action';
	  };

const WelcomeContentAction = (props: WelcomeContentActionProps) => {
	const intl = useIntl();

	switch (props.type) {
		case 'action': {
			const { onGetStarted } = props;
			return (
				<Button appearance="primary" type="submit" onClick={onGetStarted} autoFocus>
					{intl.formatMessage(messages.getStarted)}
				</Button>
			);
		}
		case 'link': {
			const { onGetStarted, href } = props;
			return (
				<LinkButton onClick={onGetStarted} href={href} appearance="primary" autoFocus>
					{intl.formatMessage(messages.getStarted)}
				</LinkButton>
			);
		}
		default:
			throw new Error('Invalid type');
	}
};

export const WelcomeContent = ({
	onClose,
	userName,
	testId,
	isVisible,
	...rest
}: WelcomeContentProps): JSX.Element => {
	const intl = useIntl();

	return (
		<Box
			xcss={cx(
				styles.contentContainerBase,
				isVisible ? styles.contentContainerVisible : styles.contentContainer,
			)}
		>
			<Heading size="large">
				{userName && intl.formatMessage(messages.greeting, { name: userName })}
				<br />
				{intl.formatMessage(messages.title)}
			</Heading>

			<Text>{intl.formatMessage(messages.description)}</Text>

			<Box xcss={styles.ctaContainer}>
				<Button appearance="default" onClick={onClose} testId={`${testId}-close`}>
					{intl.formatMessage(messages.close)}
				</Button>
				<WelcomeContentAction {...rest} />
			</Box>
		</Box>
	);
};
